import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TicketAssignmentRule,
  TicketAssignmentRuleRequest,
  TicketCannedResponse,
  TicketCannedResponseRequest,
  TicketCategory,
  TicketCategoryRequest,
  TicketCustomField,
  TicketCustomFieldRequest,
  TicketEscalationRule,
  TicketEscalationRuleRequest,
  TicketPipeline,
  TicketPipelineRequest,
  TicketPipelineStage,
  TicketPipelineStageRequest,
  TicketPriority,
  TicketPriorityRequest,
  TicketRootCause,
  TicketRootCauseRequest,
  TicketSavedView,
  TicketSavedViewRequest,
  TicketSlaPolicy,
  TicketSlaPolicyRequest,
  TicketSlaTarget,
  TicketSlaTargetRequest,
  TicketStatus,
  TicketStatusRequest,
  TicketTeam,
  TicketTeamMember,
  TicketTeamMemberRequest,
  TicketTeamRequest
} from '../models/tickets-config.model';

/**
 * Servicio HTTP para catálogos administrables del módulo Tickets.
 *
 * Todos los endpoints son per-tenant (businessId del JWT).
 * Lecturas son rol User; escrituras son rol Admin.
 *
 * Cubre Fase 1: priorities, statuses, categories, root-causes, pipelines, stages.
 * Fase 5+: sumar custom-fields, teams, assignment-rules, sla-policies, etc.
 */
@Injectable({ providedIn: 'root' })
export class TicketsConfigApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) {}

  // ==========================================================================
  // Priorities — /api/ticket-priorities
  // ==========================================================================

  getPriorities(): Observable<TicketPriority[]> {
    return this.http.get<TicketPriority[]>(`${this.apiUrl}/ticket-priorities`);
  }

  getPriorityById(id: number): Observable<TicketPriority> {
    return this.http.get<TicketPriority>(`${this.apiUrl}/ticket-priorities/${id}`);
  }

  createPriority(req: TicketPriorityRequest): Observable<TicketPriority> {
    return this.http.post<TicketPriority>(`${this.apiUrl}/ticket-priorities`, req);
  }

  updatePriority(id: number, req: TicketPriorityRequest): Observable<TicketPriority> {
    return this.http.put<TicketPriority>(`${this.apiUrl}/ticket-priorities/${id}`, req);
  }

  deletePriority(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-priorities/${id}`);
  }

  // ==========================================================================
  // Statuses — /api/ticket-statuses
  // ==========================================================================

  getStatuses(): Observable<TicketStatus[]> {
    return this.http.get<TicketStatus[]>(`${this.apiUrl}/ticket-statuses`);
  }

  getStatusById(id: number): Observable<TicketStatus> {
    return this.http.get<TicketStatus>(`${this.apiUrl}/ticket-statuses/${id}`);
  }

  createStatus(req: TicketStatusRequest): Observable<TicketStatus> {
    return this.http.post<TicketStatus>(`${this.apiUrl}/ticket-statuses`, req);
  }

  updateStatus(id: number, req: TicketStatusRequest): Observable<TicketStatus> {
    return this.http.put<TicketStatus>(`${this.apiUrl}/ticket-statuses/${id}`, req);
  }

  deleteStatus(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-statuses/${id}`);
  }

  /**
   * POST /api/ticket-statuses/reorder?businessId= — body: ids en el nuevo orden (lista COMPLETA).
   * A diferencia del resto de este servicio, este endpoint SÍ exige `businessId` explícito;
   * el llamador lo toma de AuthService.getCurrentUser(). Responde 204 sin body.
   */
  reorderStatuses(businessId: number, orderedIds: number[]): Observable<void> {
    const params = new HttpParams().set('businessId', String(businessId));
    return this.http.post<void>(`${this.apiUrl}/ticket-statuses/reorder`, orderedIds, { params });
  }

  // ==========================================================================
  // Categories — /api/ticket-categories (jerárquica)
  // ==========================================================================

  getCategories(): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(`${this.apiUrl}/ticket-categories`);
  }

  getCategoryRoots(): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(`${this.apiUrl}/ticket-categories/roots`);
  }

  getCategoryChildren(parentId: number): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(`${this.apiUrl}/ticket-categories/${parentId}/children`);
  }

  getCategoryById(id: number): Observable<TicketCategory> {
    return this.http.get<TicketCategory>(`${this.apiUrl}/ticket-categories/${id}`);
  }

  createCategory(req: TicketCategoryRequest): Observable<TicketCategory> {
    return this.http.post<TicketCategory>(`${this.apiUrl}/ticket-categories`, req);
  }

  updateCategory(id: number, req: TicketCategoryRequest): Observable<TicketCategory> {
    return this.http.put<TicketCategory>(`${this.apiUrl}/ticket-categories/${id}`, req);
  }

  deleteCategory(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-categories/${id}`);
  }

  // ==========================================================================
  // Root Causes — /api/ticket-root-causes (jerárquica)
  // ==========================================================================

  getRootCauses(): Observable<TicketRootCause[]> {
    return this.http.get<TicketRootCause[]>(`${this.apiUrl}/ticket-root-causes`);
  }

  getRootCauseRoots(): Observable<TicketRootCause[]> {
    return this.http.get<TicketRootCause[]>(`${this.apiUrl}/ticket-root-causes/roots`);
  }

  getRootCauseChildren(parentId: number): Observable<TicketRootCause[]> {
    return this.http.get<TicketRootCause[]>(`${this.apiUrl}/ticket-root-causes/${parentId}/children`);
  }

  getRootCauseById(id: number): Observable<TicketRootCause> {
    return this.http.get<TicketRootCause>(`${this.apiUrl}/ticket-root-causes/${id}`);
  }

  createRootCause(req: TicketRootCauseRequest): Observable<TicketRootCause> {
    return this.http.post<TicketRootCause>(`${this.apiUrl}/ticket-root-causes`, req);
  }

  updateRootCause(id: number, req: TicketRootCauseRequest): Observable<TicketRootCause> {
    return this.http.put<TicketRootCause>(`${this.apiUrl}/ticket-root-causes/${id}`, req);
  }

  deleteRootCause(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-root-causes/${id}`);
  }

  // ==========================================================================
  // Pipelines — /api/ticket-pipelines
  // ==========================================================================

  getPipelines(): Observable<TicketPipeline[]> {
    return this.http.get<TicketPipeline[]>(`${this.apiUrl}/ticket-pipelines`);
  }

  getDefaultPipeline(): Observable<TicketPipeline> {
    return this.http.get<TicketPipeline>(`${this.apiUrl}/ticket-pipelines/default`);
  }

  getPipelineById(id: number): Observable<TicketPipeline> {
    return this.http.get<TicketPipeline>(`${this.apiUrl}/ticket-pipelines/${id}`);
  }

  createPipeline(req: TicketPipelineRequest): Observable<TicketPipeline> {
    return this.http.post<TicketPipeline>(`${this.apiUrl}/ticket-pipelines`, req);
  }

  updatePipeline(id: number, req: TicketPipelineRequest): Observable<TicketPipeline> {
    return this.http.put<TicketPipeline>(`${this.apiUrl}/ticket-pipelines/${id}`, req);
  }

  deletePipeline(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-pipelines/${id}`);
  }

  // ==========================================================================
  // Pipeline Stages — /api/ticket-pipelines/{pipelineId}/stages + /api/ticket-pipeline-stages/{id}
  // ==========================================================================

  getPipelineStages(pipelineId: number): Observable<TicketPipelineStage[]> {
    return this.http.get<TicketPipelineStage[]>(`${this.apiUrl}/ticket-pipelines/${pipelineId}/stages`);
  }

  createPipelineStage(pipelineId: number, req: TicketPipelineStageRequest): Observable<TicketPipelineStage> {
    return this.http.post<TicketPipelineStage>(`${this.apiUrl}/ticket-pipelines/${pipelineId}/stages`, req);
  }

  getPipelineStageById(id: number): Observable<TicketPipelineStage> {
    return this.http.get<TicketPipelineStage>(`${this.apiUrl}/ticket-pipeline-stages/${id}`);
  }

  updatePipelineStage(id: number, req: TicketPipelineStageRequest): Observable<TicketPipelineStage> {
    return this.http.put<TicketPipelineStage>(`${this.apiUrl}/ticket-pipeline-stages/${id}`, req);
  }

  deletePipelineStage(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-pipeline-stages/${id}`);
  }

  // ==========================================================================
  // Canned Responses — /api/ticket-canned-responses (RF-06.02)
  // ==========================================================================

  getCannedResponses(): Observable<TicketCannedResponse[]> {
    return this.http.get<TicketCannedResponse[]>(`${this.apiUrl}/ticket-canned-responses`);
  }

  getCannedResponseById(id: number): Observable<TicketCannedResponse> {
    return this.http.get<TicketCannedResponse>(`${this.apiUrl}/ticket-canned-responses/${id}`);
  }

  createCannedResponse(req: TicketCannedResponseRequest): Observable<TicketCannedResponse> {
    return this.http.post<TicketCannedResponse>(`${this.apiUrl}/ticket-canned-responses`, req);
  }

  updateCannedResponse(id: number, req: TicketCannedResponseRequest): Observable<TicketCannedResponse> {
    return this.http.put<TicketCannedResponse>(`${this.apiUrl}/ticket-canned-responses/${id}`, req);
  }

  deleteCannedResponse(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-canned-responses/${id}`);
  }

  // ==========================================================================
  // Saved Views — /api/ticket-saved-views (RF-13.05)
  // ==========================================================================

  getSavedViews(): Observable<TicketSavedView[]> {
    return this.http.get<TicketSavedView[]>(`${this.apiUrl}/ticket-saved-views`);
  }

  getSavedViewById(id: number): Observable<TicketSavedView> {
    return this.http.get<TicketSavedView>(`${this.apiUrl}/ticket-saved-views/${id}`);
  }

  createSavedView(req: TicketSavedViewRequest): Observable<TicketSavedView> {
    return this.http.post<TicketSavedView>(`${this.apiUrl}/ticket-saved-views`, req);
  }

  updateSavedView(id: number, req: TicketSavedViewRequest): Observable<TicketSavedView> {
    return this.http.put<TicketSavedView>(`${this.apiUrl}/ticket-saved-views/${id}`, req);
  }

  deleteSavedView(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-saved-views/${id}`);
  }

  // ==========================================================================
  // Teams + Members — /api/ticket-teams + /api/ticket-team-members
  // ==========================================================================

  getTeams(): Observable<TicketTeam[]> {
    return this.http.get<TicketTeam[]>(`${this.apiUrl}/ticket-teams`);
  }

  getTeamById(id: number): Observable<TicketTeam> {
    return this.http.get<TicketTeam>(`${this.apiUrl}/ticket-teams/${id}`);
  }

  createTeam(req: TicketTeamRequest): Observable<TicketTeam> {
    return this.http.post<TicketTeam>(`${this.apiUrl}/ticket-teams`, req);
  }

  updateTeam(id: number, req: TicketTeamRequest): Observable<TicketTeam> {
    return this.http.put<TicketTeam>(`${this.apiUrl}/ticket-teams/${id}`, req);
  }

  deleteTeam(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-teams/${id}`);
  }

  getTeamMembers(teamId: number): Observable<TicketTeamMember[]> {
    return this.http.get<TicketTeamMember[]>(`${this.apiUrl}/ticket-teams/${teamId}/members`);
  }

  addTeamMember(teamId: number, req: TicketTeamMemberRequest): Observable<TicketTeamMember> {
    return this.http.post<TicketTeamMember>(`${this.apiUrl}/ticket-teams/${teamId}/members`, req);
  }

  updateTeamMember(memberId: number, req: TicketTeamMemberRequest): Observable<TicketTeamMember> {
    return this.http.put<TicketTeamMember>(`${this.apiUrl}/ticket-team-members/${memberId}`, req);
  }

  removeTeamMember(memberId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-team-members/${memberId}`);
  }

  // ==========================================================================
  // SLA Policies + Targets — /api/sla-policies + /api/sla-targets
  // ==========================================================================

  getSlaPolicies(): Observable<TicketSlaPolicy[]> {
    return this.http.get<TicketSlaPolicy[]>(`${this.apiUrl}/sla-policies`);
  }

  getSlaPolicyById(id: number): Observable<TicketSlaPolicy> {
    return this.http.get<TicketSlaPolicy>(`${this.apiUrl}/sla-policies/${id}`);
  }

  createSlaPolicy(req: TicketSlaPolicyRequest): Observable<TicketSlaPolicy> {
    return this.http.post<TicketSlaPolicy>(`${this.apiUrl}/sla-policies`, req);
  }

  updateSlaPolicy(id: number, req: TicketSlaPolicyRequest): Observable<TicketSlaPolicy> {
    return this.http.put<TicketSlaPolicy>(`${this.apiUrl}/sla-policies/${id}`, req);
  }

  deleteSlaPolicy(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sla-policies/${id}`);
  }

  getSlaTargets(policyId: number): Observable<TicketSlaTarget[]> {
    return this.http.get<TicketSlaTarget[]>(`${this.apiUrl}/sla-policies/${policyId}/targets`);
  }

  createSlaTarget(policyId: number, req: TicketSlaTargetRequest): Observable<TicketSlaTarget> {
    return this.http.post<TicketSlaTarget>(`${this.apiUrl}/sla-policies/${policyId}/targets`, req);
  }

  updateSlaTarget(targetId: number, req: TicketSlaTargetRequest): Observable<TicketSlaTarget> {
    return this.http.put<TicketSlaTarget>(`${this.apiUrl}/sla-targets/${targetId}`, req);
  }

  deleteSlaTarget(targetId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sla-targets/${targetId}`);
  }

  // ==========================================================================
  // Custom Fields — /api/ticket-custom-fields (RF-02.02)
  // ==========================================================================

  getCustomFields(): Observable<TicketCustomField[]> {
    return this.http.get<TicketCustomField[]>(`${this.apiUrl}/ticket-custom-fields`);
  }

  getCustomFieldById(id: number): Observable<TicketCustomField> {
    return this.http.get<TicketCustomField>(`${this.apiUrl}/ticket-custom-fields/${id}`);
  }

  createCustomField(req: TicketCustomFieldRequest): Observable<TicketCustomField> {
    return this.http.post<TicketCustomField>(`${this.apiUrl}/ticket-custom-fields`, req);
  }

  updateCustomField(id: number, req: TicketCustomFieldRequest): Observable<TicketCustomField> {
    return this.http.put<TicketCustomField>(`${this.apiUrl}/ticket-custom-fields/${id}`, req);
  }

  deleteCustomField(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-custom-fields/${id}`);
  }

  // ==========================================================================
  // Assignment Rules — /api/ticket-assignment-rules (RF-07.03)
  // ==========================================================================

  getAssignmentRules(): Observable<TicketAssignmentRule[]> {
    return this.http.get<TicketAssignmentRule[]>(`${this.apiUrl}/ticket-assignment-rules`);
  }

  getAssignmentRuleById(id: number): Observable<TicketAssignmentRule> {
    return this.http.get<TicketAssignmentRule>(`${this.apiUrl}/ticket-assignment-rules/${id}`);
  }

  createAssignmentRule(req: TicketAssignmentRuleRequest): Observable<TicketAssignmentRule> {
    return this.http.post<TicketAssignmentRule>(`${this.apiUrl}/ticket-assignment-rules`, req);
  }

  updateAssignmentRule(id: number, req: TicketAssignmentRuleRequest): Observable<TicketAssignmentRule> {
    return this.http.put<TicketAssignmentRule>(`${this.apiUrl}/ticket-assignment-rules/${id}`, req);
  }

  deleteAssignmentRule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/ticket-assignment-rules/${id}`);
  }

  // ==========================================================================
  // Escalation Rules — /api/escalation-rules (RF-04.08)
  // ==========================================================================

  getEscalationRules(): Observable<TicketEscalationRule[]> {
    return this.http.get<TicketEscalationRule[]>(`${this.apiUrl}/escalation-rules`);
  }

  getEscalationRuleById(id: number): Observable<TicketEscalationRule> {
    return this.http.get<TicketEscalationRule>(`${this.apiUrl}/escalation-rules/${id}`);
  }

  createEscalationRule(req: TicketEscalationRuleRequest): Observable<TicketEscalationRule> {
    return this.http.post<TicketEscalationRule>(`${this.apiUrl}/escalation-rules`, req);
  }

  updateEscalationRule(id: number, req: TicketEscalationRuleRequest): Observable<TicketEscalationRule> {
    return this.http.put<TicketEscalationRule>(`${this.apiUrl}/escalation-rules/${id}`, req);
  }

  deleteEscalationRule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/escalation-rules/${id}`);
  }

}
